import {
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { LOCAL_STORAGE, WINDOW } from '@ng-web-apis/common';

/** Below this width the sidebar is a drawer over the page. */
export const SIDEBAR_OVERLAY_BREAKPOINT = 1024;
const EXPANDED_KEY = 'sidebarExpanded';

@Component({
  selector: 'app-side-menu-layout',
  templateUrl: './side-menu-layout.component.html',
  styleUrls: ['./side-menu-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SideMenuLayoutComponent {
  private windowRef = inject<Window>(WINDOW);
  private storage = inject<Storage>(LOCAL_STORAGE);
  private changeDetector = inject(ChangeDetectorRef);

  @Input() closeOnOutsideClick = false;
  /**
   * shrink: the sidebar sits next to the page, expanded or as an icon rail;
   * overlap: it is a drawer that covers the page.
   */
  @Input() openedStateMode: 'shrink' | 'overlap' = 'shrink';
  /** Expanded sidebar (shrink) or open drawer (overlap). */
  @Input() menuOpened = true;
  /** Shades the page with a load indicator, e.g. while a list loads. */
  @Input() loading = false;
  /** The loading is a search: a slow one shows its elapsed time. */
  @Input() loadingElapsed = false;
  @Output() menuOpenedChange = new EventEmitter<boolean>();

  constructor() {
    this.applyWidth();
  }

  /** The side menu shows only its icons while the sidebar is a rail. */
  @HostBinding('class.sidebar-rail') get rail() {
    return this.openedStateMode === 'shrink' && !this.menuOpened;
  }

  @HostListener('window:resize')
  onWindowResize() {
    const overlap = this.isNarrow();
    if (overlap !== (this.openedStateMode === 'overlap')) {
      this.applyWidth();
      this.changeDetector.markForCheck();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.openedStateMode === 'overlap' && this.menuOpened) {
      this.setOpened(false);
    }
  }

  toggleMenu = () => {
    this.setOpened(!this.menuOpened);
  };

  /** In overlap mode a click next to the open menu closes it. */
  onContentClick() {
    if (this.closeOnOutsideClick && this.menuOpened) {
      this.setOpened(false);
    }
  }

  /** A link in the drawer was followed; the drawer gets out of the way. */
  navigated() {
    if (this.openedStateMode === 'overlap' && this.menuOpened) {
      this.setOpened(false);
    }
  }

  /** Expands the rail, e.g. to show the lists behind a section icon. */
  expand() {
    if (!this.menuOpened) {
      this.setOpened(true);
    }
  }

  private isNarrow() {
    return this.windowRef.innerWidth < SIDEBAR_OVERLAY_BREAKPOINT;
  }

  private applyWidth() {
    const narrow = this.isNarrow();
    this.openedStateMode = narrow ? 'overlap' : 'shrink';
    this.closeOnOutsideClick = narrow;
    this.menuOpened = narrow ? false : this.readExpanded();
  }

  /** toggleMenu comes from the header of the parent page, which does not mark this view. */
  private setOpened(opened: boolean) {
    this.menuOpened = opened;
    if (this.openedStateMode === 'shrink') {
      this.writeExpanded(opened);
    }
    this.changeDetector.markForCheck();
    this.menuOpenedChange.emit(opened);
  }

  // The storage can be missing or throw (private mode, blocked site data).
  private readExpanded() {
    try {
      return this.storage.getItem(EXPANDED_KEY) !== 'false';
    } catch {
      return true;
    }
  }

  private writeExpanded(expanded: boolean) {
    try {
      this.storage.setItem(EXPANDED_KEY, String(expanded));
    } catch {
      // The state is only a convenience.
    }
  }
}
